import { Alert, AlertTitle, Box, Paper, useTheme } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import { useDatasetMetadataKeys } from "@/shared/domain/datasets/hooks";
import { useDatasetTablePreferences } from "@/shared/domain/users/hooks";
import { RobotoDomainException } from "@/shared/http/exceptions";
import { Dataset, SearchQueryBody } from "@/types";

import { RobotoDataGrid } from "../RobotoDataGrid";

import { ColumnsModal } from "./ColumnsModal";
import { TablePageState, tablePageReducer } from "./stateReducer";
import { createRows, mergeMetadataColumns } from "./tableLogic";
import type { BaseTableProps } from "./tableProps";

const possibleStandardDatasetColumns = [
  "dataset_id",
  "created",
  "created_by",
  "tags",
  "administrator",
  "modified",
  "modified_by",
  "name",
  "org_id",
  "storage_location",
  "device_id",
  "source",
];

const sortableColumns = new Set<string>();

const initialQuery: SearchQueryBody = {
  sort_by: "created",
  sort_direction: "DESC",
};

const createTableState = (
  columns: string[],
  rowsPerPage: number = 25,
): TablePageState => ({
  modalOpen: false,
  rightClickedRow: null,
  sidebar: { isOpen: false, dataset: null },
  columns,
  rowsPerPage: rowsPerPage as 10 | 25 | 50,
  snackbar: { isOpen: false, message: "" },
  sortColumnIndex: 1,
  sortOrder: "descending",
  lastSearchQuery: initialQuery,
  selectedRows: new Set<string>(),
  preferencesLoaded: false,
});

interface DatasetsTableProps extends BaseTableProps<Dataset> {
  selectedDatasets?: Set<string>;
  setSelectedDatasets?: (arg: Set<string>) => void;
  onDatasetSingleClick?: (dataset: Dataset) => void;
  variant?: "elevation" | "outlined";
}

export const DatasetsTable: React.FC<DatasetsTableProps> = ({
  data: datasets,
  dataCount,
  loading,
  selectedDatasets,
  setSelectedDatasets = () => {},
  onDatasetSingleClick = () => {},
  variant,
  containerStyle,
  fetchMoreData,
}) => {
  const theme = useTheme();
  const {
    preferences,
    saveColumns,
    saveRowsPerPage,
    isLoading: isLoadingPreferences,
    error,
    clearError,
  } = useDatasetTablePreferences();

  const [state, dispatch] = React.useReducer(
    tablePageReducer,
    createTableState(preferences.columns, preferences.rowsPerPage),
  );

  const [page, setPage] = React.useState(0);

  const { data: metadataKeys } = useDatasetMetadataKeys();

  const datasetsById = new Map<string, Dataset>(
    datasets.map((dataset) => {
      return [dataset.dataset_id, dataset];
    }),
  );

  const { goto } = useNavigation();
  const [selectedRowsToggle, setSelectedRowsToggle] =
    React.useState<boolean>(false);

  const pageData = React.useMemo(() => {
    return datasets.slice(
      page * state.rowsPerPage,
      page * state.rowsPerPage + state.rowsPerPage,
    );
  }, [datasets, state.rowsPerPage, page]);

  const { rows, standardColumns, metadataColumns } = React.useMemo(() => {
    const possibleMetadataColumns: string[] =
      mergeMetadataColumns(pageData, metadataKeys ?? []) ?? [];

    const rows = createRows(pageData, state.columns, possibleMetadataColumns);

    // Sort standard columns alphabetically
    const standardColumns = [...possibleStandardDatasetColumns].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );

    // Sort metadata columns alphabetically
    const metadataColumns = [...possibleMetadataColumns].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );

    return { rows, standardColumns, metadataColumns };
  }, [pageData, state.columns, metadataKeys]);

  const noSearchResults =
    Object.keys(state.lastSearchQuery).length > 0 && datasets.length === 0;

  React.useEffect(() => {
    if (!isLoadingPreferences && !state.preferencesLoaded) {
      dispatch({
        type: "LOAD_PREFERENCES",
        payload: {
          columns: preferences.columns,
          rowsPerPage: preferences.rowsPerPage as 10 | 25 | 50,
        },
      });
    }
  }, [
    isLoadingPreferences,
    preferences.columns,
    preferences.rowsPerPage,
    state.preferencesLoaded,
  ]);

  React.useEffect(() => {
    if (selectedDatasets && selectedDatasets.size === 0) {
      setSelectedRowsToggle((prevToggle) => !prevToggle);
      dispatch({
        type: "SET_SELECTED_ROWS",
        payload: { selectedRows: new Set<string>() },
      });
    }
  }, [dispatch, selectedDatasets]);

  if (!state.preferencesLoaded) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
      }}
    >
      <Box
        sx={{
          flex: 1,
          maxWidth: "100%",
        }}
      >
        <Paper
          sx={{
            mt: theme.spacing(2),
          }}
          variant={variant}
          elevation={0}
        >
          {error && (
            <Alert
              severity="error"
              onClose={clearError}
              sx={{ mb: theme.spacing(2) }}
            >
              {error instanceof RobotoDomainException
                ? error.message
                : "An unexpected error occurred while saving your preferences. Please try again later."}
            </Alert>
          )}

          {noSearchResults && (
            <Alert severity="info">
              <AlertTitle>Datasets</AlertTitle>
              <>
                No datasets match your search criteria. Try changing your search
                terms and try again.
              </>
            </Alert>
          )}

          {!noSearchResults && (
            <>
              <RobotoDataGrid
                sortableColumns={sortableColumns}
                sortingOrder={state.sortOrder}
                sortingColumnIndex={state.sortColumnIndex}
                page={page}
                rowsPerPage={state.rowsPerPage}
                nextPageButtonDisabled={
                  dataCount !== undefined
                    ? (page + 1) * state.rowsPerPage >= dataCount
                    : true
                }
                containerStyle={containerStyle}
                loading={loading}
                columnNames={state.columns}
                rows={rows}
                totalRowCount={dataCount}
                onPageChange={(newPage, rowsPerPage) => {
                  if ((newPage + 2) * rowsPerPage >= datasets.length) {
                    // Request more pages of data from the backend
                    // as we approach the last page of cached results.
                    // 2 pages prior seemed to work well experimentally.
                    fetchMoreData?.();
                  }
                  setPage(newPage);
                }}
                onRowsPerPageChange={(_page, newRowsPerPage) => {
                  setPage(0);
                  dispatch({
                    type: "SET_TABLE_CONFIG",
                    payload: {
                      rowsPerPage: newRowsPerPage,
                    },
                  });
                  void saveRowsPerPage(newRowsPerPage);
                }}
                onColumnSortClicked={() => {
                  // do nothing
                }}
                onAddColumnClick={() => {
                  dispatch({ type: "SET_MODAL_OPEN", payload: true });
                }}
                onRowDoubleClick={(rowId) => {
                  goto.dataset({ datasetId: rowId });
                }}
                onRowSingleClick={(rowId: string) => {
                  const datasetRecord = datasetsById.get(rowId);
                  if (onDatasetSingleClick && datasetRecord) {
                    onDatasetSingleClick(datasetRecord);
                  }
                }}
                onSelectedRowsChange={(selectedRows) => {
                  setSelectedDatasets(selectedRows);
                  dispatch({
                    type: "SET_SELECTED_ROWS",
                    payload: { selectedRows: selectedRows },
                  });
                }}
                isRowSelectable={true}
                isRowExpandable={false}
                clearSelectedRowsToggle={selectedRowsToggle}
              />
              <ColumnsModal
                open={state.modalOpen}
                handleClose={() => {
                  dispatch({ type: "SET_MODAL_OPEN", payload: false });
                }}
                standardColumns={standardColumns}
                metadataColumns={metadataColumns}
                currentColumns={state.columns}
                onSave={(updatedColumns) => {
                  dispatch({ type: "SAVE_COLUMNS", payload: updatedColumns });
                  void saveColumns(updatedColumns);
                }}
              />
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
